import { useCallback, useEffect, useRef, useState } from "react";
import type { UnitGroupConfig, UnitGroupingMode } from "@autodromo/units";
import { WhatsApp } from "../icons/WhatsApp";
import { X } from "../icons/X";
import {
  WhatsappDropdown,
  WhatsappHierarchyNode,
} from "../WhatsappDropdown/WhatsappDropdown";
import type { PageEntry } from "../../utils/pageEntry";
import type { useGlobalQueryParams as useGlobalQueryParamsHook } from "../../hooks/queryParams/useGlobalQueryParams";
import {
  FloatingButtonContainer,
  FloatingButton,
  DropdownWrapper,
  IconWrapper,
} from "./WhatsappFloatingButton.styles";
import { AntiBotMeta } from "../../constants/antiBot";

export type FloatingButtonPosition = "BOTTOM_RIGHT" | "BOTTOM_LEFT";

export interface WhatsappFloatingButtonProps {
  items: WhatsappHierarchyNode[];
  position?: FloatingButtonPosition;
  buttonColor?: string;
  iconColor?: string;
  isVisible?: boolean;
  onClick?: (phoneNumber: string, item?: WhatsappHierarchyNode) => void;
  message?: string;
  submitUrl?: string;
  antiBot: AntiBotMeta | null;
  accountId?: string;
  siteId?: string;
  product?: string;
  units?: { id: string; name: string; displayName: string }[];
  unitGroupingMode?: UnitGroupingMode | null;
  unitGroups?: UnitGroupConfig[] | null;
  useGlobalQueryParams?: typeof useGlobalQueryParamsHook;
  pageEntry?: PageEntry | null;
}

export const WhatsappFloatingButton = ({
  items,
  position = "BOTTOM_RIGHT",
  buttonColor = "#25D366",
  iconColor = "#ffffff",
  isVisible = true,
  onClick,
  message,
  submitUrl,
  antiBot,
  accountId,
  siteId,
  product,
  units,
  unitGroupingMode,
  unitGroups,
  useGlobalQueryParams,
  pageEntry,
}: WhatsappFloatingButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isEditor =
    typeof window !== "undefined" && window.location.href.includes("/builder");

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, handleClickOutside]);

  const handleItemClick = useCallback(
    (phoneNumber: string, item?: WhatsappHierarchyNode) => {
      onClick?.(phoneNumber, item);
      setIsOpen(false);
    },
    [onClick],
  );

  if (!isVisible || !items || items.length === 0) {
    return null;
  }

  return (
    <FloatingButtonContainer
      ref={containerRef}
      $position={position}
      $isEditor={isEditor}
    >
      {isOpen && (
        <DropdownWrapper>
          <WhatsappDropdown
            items={items}
            onClick={handleItemClick}
            message={message}
            submitUrl={submitUrl}
            antiBot={antiBot}
            accountId={accountId}
            siteId={siteId}
            product={product}
            units={units}
            unitGroupingMode={unitGroupingMode}
            unitGroups={unitGroups}
            useGlobalQueryParams={useGlobalQueryParams}
            pageEntry={pageEntry}
            isEditor={isEditor}
          />
        </DropdownWrapper>
      )}
      <FloatingButton
        type="button"
        onClick={toggleDropdown}
        $buttonColor={buttonColor}
        aria-label={isOpen ? "Fechar WhatsApp" : "Abrir WhatsApp"}
        title={isOpen ? "Fechar" : "Fale conosco pelo WhatsApp"}
        data-gtag-action="Clique Flutuante"
        data-gtag-category="WhatsApp"
      >
        <IconWrapper $isOpen={isOpen}>
          {isOpen ? (
            <X
              color={iconColor}
              size={42}
            />
          ) : (
            <WhatsApp color={iconColor} />
          )}
        </IconWrapper>
      </FloatingButton>
    </FloatingButtonContainer>
  );
};
